import { useEffect, useRef, useMemo, useState } from 'react'
import { useTranslation } from 'react-i18next'
import {
  message,
  Tag,
  Input,
  Space,
  Button,
  Upload,
  Checkbox,
  Typography,
  Modal,
  Select,
} from 'antd'
import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query'
import {
  DownloadOutlined,
  SearchOutlined,
  UploadOutlined,
  EditOutlined,
} from '@ant-design/icons'

import {
  fetchAllServiceProviderAdditiveMaterials,
  uploadServiceProviderMaterialSpecFile,
  downloadServiceProviderMaterialSpecFile,
  archiveServiceProviderAdditiveMaterial,
  updateServiceProviderMaterial,
  fetchAllColors,
} from '$api/evoAPIs'

import { dateWithTimeRenderer } from '$utils/i18n'
import { UilArchiveAlt } from '@iconscout/react-unicons'
import { StyledTable } from '$pages/serviceuser/Mymaterials/MaterialMapper/styledComponents'
import Tooltip from '$components/Tooltip'
import { useTheme } from 'styled-components'
import Form from '$components/Form'
import { useUser } from '$context/user'

const ServiceProviderAdditiveMaterialsTable = ({
  onMaterialSelect,
  selectedMaterialIdForMapping,
  highlightedMaterialId,
  onHighlightComplete,
  operatorAdditiveMaterialNameMap,
  selectedOperatorAdditiveMaterialIds,
  listActionTrigger,
}) => {
  const tableRef = useRef(null)
  const { t } = useTranslation()
  const queryClient = useQueryClient()
  const theme = useTheme()
  const [form] = Form.useForm()
  const user = useUser()

  const [pagination, setPagination] = useState({
    current: 1,
    pageSize: 10,
    total: 0,
  })

  const [sorter, setSorter] = useState({ field: 'name', order: 'ascend' })
  const [searchText, setSearchText] = useState('')

  const [isEditModalOpen, setIsEditModalOpen] = useState(false)
  const [editingMaterial, setEditingMaterial] = useState(null)

  const { data: allColors } = useQuery(['colors'], fetchAllColors)

  const {
    data: serviceProviderMaterialsData,
    isLoading: isLoadingServiceProviderMaterials,
  } = useQuery(
    ['serviceProviderMaterialsForMapperPage'],
    async () => {
      let allMaterials = []
      let currentPage = 1
      let hasMorePages = true
      let totalCount = 0

      while (hasMorePages) {
        const pageData = await fetchAllServiceProviderAdditiveMaterials({
          queryKey: [
            'serviceProviderMaterialsForMapperPage',
            { page: currentPage, size: 100 },
          ],
        })

        if (pageData.items && pageData.items.length > 0) {
          allMaterials = [...allMaterials, ...pageData.items]
          totalCount = pageData.total

          const totalFetched = currentPage * 100
          hasMorePages = totalFetched < pageData.total
          currentPage += 1
        } else {
          hasMorePages = false
        }
      }

      return {
        items: allMaterials,
        size: allMaterials.length,
        total: totalCount,
      }
    },
    {
      staleTime: 5 * 60 * 1000, // 5 minutes
      gcTime: 10 * 60 * 1000, // 10 minutes
    },
  )

  const sortedAndFilteredData = useMemo(() => {
    const items = serviceProviderMaterialsData?.items || []

    if (
      !selectedOperatorAdditiveMaterialIds ||
      selectedOperatorAdditiveMaterialIds.size === 0
    ) {
      return items.filter((item) =>
        item.material_name.toLowerCase().includes(searchText.toLowerCase()),
      )
    }

    const highlighted = []
    const others = []

    items.forEach((item) => {
      const itemMappedIds = new Set(
        (item.mapped_operator_additive_material_ids || []).map((id) =>
          String(id),
        ),
      )

      let isHighlighted = false
      for (const selectedId of selectedOperatorAdditiveMaterialIds) {
        if (itemMappedIds.has(selectedId)) {
          isHighlighted = true
          break
        }
      }

      if (isHighlighted) {
        highlighted.push(item)
      } else {
        others.push(item)
      }
    })

    return [...highlighted, ...others].filter((item) =>
      item.material_name.toLowerCase().includes(searchText.toLowerCase()),
    )
  }, [
    selectedOperatorAdditiveMaterialIds,
    serviceProviderMaterialsData,
    searchText,
  ])

  const downloadMutation = useMutation({
    mutationFn: downloadServiceProviderMaterialSpecFile,
    onSuccess: ({ blob, filename }) => {
      const url = window.URL.createObjectURL(blob)
      const a = document.createElement('a')
      a.href = url
      a.download = filename
      document.body.appendChild(a)
      a.click()
      a.remove()
      window.URL.revokeObjectURL(url)
      message.success(t('download_successful'))
    },
    onError: () => {
      message.error(t('download_failed'))
    },
  })

  const handleDownload = (materialId) => {
    downloadMutation.mutate(materialId)
  }

  const uploadMutation = useMutation({
    mutationFn: uploadServiceProviderMaterialSpecFile,
    onSuccess: () => {
      message.success(t('spec_file_upload_success'))

      queryClient.invalidateQueries({
        queryKey: ['serviceProviderMaterialsForMapperPage'],
      })
      queryClient.refetchQueries({
        queryKey: ['serviceProviderMaterialsForMapperPage'],
      })
      queryClient.invalidateQueries({
        queryKey: ['totalServiceProviderMaterials'],
      })
    },
    onError: (error) => {
      message.error(
        error.response?.data?.detail ||
          t('spec_file_upload_error', 'Failed to upload file.'),
      )
    },
  })

  const archiveMutation = useMutation({
    mutationFn: archiveServiceProviderAdditiveMaterial,
    onSuccess: (data) => {
      if (data.is_archived) {
        message.success(t('successfully_archived_material'))
      } else {
        message.success(t('successfully_unarchived_material'))
      }

      queryClient.invalidateQueries({
        queryKey: ['serviceProviderMaterialsForMapperPage'],
      })
      queryClient.refetchQueries({
        queryKey: ['serviceProviderMaterialsForMapperPage'],
      })
      queryClient.invalidateQueries({ queryKey: ['dwhMaterialsListForKpi'] })
    },
    onError: (error) => {
      message.error(error.response?.data?.detail || error.message)
    },
  })

  const updateMutation = useMutation({
    mutationFn: updateServiceProviderMaterial,
    onSuccess: () => {
      message.success(t('service_provider_additive_materials.material_updated'))
      setIsEditModalOpen(false)
      setEditingMaterial(null)
      queryClient.invalidateQueries(['serviceProviderMaterialsForMapperPage'])
      queryClient.refetchQueries(['serviceProviderMaterialsForMapperPage'])
    },
    onError: () => {
      message.error(t('error_updating_material', 'Error updating material'))
    },
  })

  const handleEditClick = (record) => {
    setEditingMaterial(record)
    form.setFieldsValue({
      material_name: record.material_name,
      available_color_ids: record.available_colors?.map((c) => c.id) || [],
    })
    setIsEditModalOpen(true)
  }

  const handleEditOk = () => {
    form.validateFields().then((values) => {
      updateMutation.mutate({
        id: editingMaterial.id,
        material_name: values.material_name,
        available_color_ids: values.available_color_ids,
      })
    })
  }

  const handleEditCancel = () => {
    setIsEditModalOpen(false)
    setEditingMaterial(null)
  }

  const getRowClassName = (record) => {
    let classNames = []

    if (highlightedMaterialId && record.id === highlightedMaterialId) {
      classNames.push('temp-highlight')
    }

    if (
      selectedMaterialIdForMapping &&
      record.id === selectedMaterialIdForMapping
    ) {
      classNames.push('selected-for-mapping-row')
    }

    const itemMappedIds = new Set(
      (record.mapped_operator_additive_material_ids || []).map(String),
    )

    if (
      selectedOperatorAdditiveMaterialIds &&
      selectedOperatorAdditiveMaterialIds.size > 0
    ) {
      for (const selectedId of selectedOperatorAdditiveMaterialIds) {
        if (itemMappedIds.has(selectedId)) {
          classNames.push('highlighted-by-operator-additive-selection')
          break
        }
      }
    }

    return classNames.join(' ')
  }

  useEffect(() => {
    if (highlightedMaterialId && serviceProviderMaterialsData) {
      const items = serviceProviderMaterialsData.items || []
      const index = items.findIndex((item) => item.id === highlightedMaterialId)

      if (index !== -1) {
        const pageNumber = Math.floor(index / pagination.pageSize) + 1

        setPagination((prev) => ({
          ...prev,
          current: pageNumber,
        }))

        setTimeout(() => {
          if (tableRef.current) {
            const rowElement = tableRef.current.querySelector(
              `.ant-table-row[data-row-key="${highlightedMaterialId}"]`,
            )
            if (rowElement) {
              rowElement.scrollIntoView({ behavior: 'smooth', block: 'center' })
              rowElement.classList.add('temp-highlight')
              setTimeout(() => {
                rowElement.classList.remove('temp-highlight')
                if (onHighlightComplete) onHighlightComplete()
              }, 2500)
            } else if (onHighlightComplete) onHighlightComplete()
          }
        }, 300)
      } else if (onHighlightComplete) {
        onHighlightComplete()
      }
    }
  }, [
    highlightedMaterialId,
    serviceProviderMaterialsData,
    pagination.pageSize,
    listActionTrigger,
    onHighlightComplete,
  ])

  const handleTableChange = (newPagination, _, newSorter) => {
    setPagination((prev) => ({
      ...prev,
      current: newPagination.current || 1,
      pageSize: newPagination.pageSize || 10,
    }))

    if (newSorter && newSorter.field) {
      if (newSorter.order) {
        setSorter({ field: newSorter.field, order: newSorter.order })
      } else {
        // Clear sorting when no order is specified
        setSorter({ field: null, order: null })
      }
    } else {
      // Reset to default sorting
      setSorter({ field: 'material_name', order: 'ascend' })
    }
  }

  const renderMappedOperatorAdditiveMaterials = (mappedIds) => {
    const idsArray = Array.isArray(mappedIds)
      ? mappedIds
      : mappedIds instanceof Set
        ? Array.from(mappedIds)
        : []

    const materialCount = idsArray.length

    if (materialCount === 0) {
      return (
        <Tooltip
          title={t(
            'no_mapped_materials',
            'No mapped materials / Keine gemappten Materialien',
          )}
        >
          <i style={{ color: '#999', cursor: 'help' }}>{t('unmapped')}</i>
        </Tooltip>
      )
    }

    const materialTags = idsArray.map((id) => {
      const stringId = String(id)
      const name = operatorAdditiveMaterialNameMap?.[stringId]
      const fullDisplayName = name || `ID: ${stringId.substring(0, 8)}...`
      const shortDisplayName =
        fullDisplayName.length > 7
          ? fullDisplayName.substring(0, 7) + '...'
          : fullDisplayName

      return (
        <Tooltip key={stringId} title={fullDisplayName}>
          <Tag color="blue" style={{ margin: '2px' }}>
            {shortDisplayName}
          </Tag>
        </Tooltip>
      )
    })

    const maxDisplayTags = 3

    if (materialTags.length <= maxDisplayTags) {
      return <div style={{ cursor: 'help' }}>{materialTags}</div>
    }

    const visibleTags = materialTags.slice(0, maxDisplayTags)
    const hiddenCount = materialTags.length - maxDisplayTags

    return (
      <div style={{ cursor: 'help' }}>
        {visibleTags}
        <Tooltip
          title={materialTags
            .slice(maxDisplayTags)
            .map((tag, index) => {
              const id = idsArray[maxDisplayTags + index]
              const stringId = String(id)
              const name = operatorAdditiveMaterialNameMap?.[stringId]
              return name || `ID: ${stringId.substring(0, 8)}...`
            })
            .join(', ')}
        >
          <Tag color="default">+{hiddenCount} more</Tag>
        </Tooltip>
      </div>
    )
  }

  const columns = [
    {
      title: t('service_provider_additive_materials.header'),
      dataIndex: 'material_name',
      key: 'material_name',
      sorter: (a, b) => a.material_name.localeCompare(b.material_name),
      sortOrder: sorter.field === 'material_name' ? sorter.order : null,
      filterDropdown: ({
        setSelectedKeys,
        selectedKeys,
        confirm,
        clearFilters,
      }) => (
        <div style={{ padding: 8 }}>
          <Input
            placeholder={t('search_placeholder', 'Search Material')}
            value={selectedKeys[0]?.text || ''}
            onChange={(e) =>
              setSelectedKeys([{ ...selectedKeys[0], text: e.target.value }])
            }
            onPressEnter={() => confirm()}
            style={{ marginBottom: 8, display: 'block' }}
          />
          <Space>
            <Tooltip title="Include Makerverse materials">
              <Checkbox
                checked={selectedKeys[0]?.checked || false}
                onChange={(e) =>
                  setSelectedKeys([
                    { ...selectedKeys[0], checked: e.target.checked },
                  ])
                }
              />
            </Tooltip>
            <Button
              type="primary"
              onClick={() => confirm()}
              icon={<SearchOutlined />}
              size="small"
            >
              {t('search_button', 'Search')}
            </Button>
            <Button
              size="small"
              onClick={() => {
                if (clearFilters) clearFilters()
                setSelectedKeys([])
                confirm()
              }}
            >
              {t('reset_button', 'Reset')}
            </Button>
          </Space>
        </div>
      ),
      filterIcon: (filtered) => (
        <Tooltip title={t('material_search_tooltip')}>
          <SearchOutlined
            style={{ color: filtered ? '#1890ff' : theme.colorPrimary }}
          />
        </Tooltip>
      ),
      onFilter: (value, record) => {
        const { text, checked } = value || {}
        const nameMatches = record.material_name
          .toLowerCase()
          .includes((text || '').toLowerCase())

        if (!text) {
          return checked ? record.printer_aggregator === 'Makerverse' : true
        }

        return checked
          ? nameMatches && record.printer_aggregator === 'Makerverse'
          : nameMatches
      },
    },
    {
      title: t(
        'operator_additive_materials.mapped_operator_additive_materials_column',
      ),
      dataIndex: 'mapped_operator_additive_material_ids',
      key: 'mapped_operator_additive_material_ids',
      sorter: (a, b) =>
        (a.mapped_operator_additive_material_ids?.length || 0) -
        (b.mapped_operator_additive_material_ids?.length || 0),
      sortOrder:
        sorter.field === 'mapped_operator_additive_material_ids'
          ? sorter.order
          : null,
      render: renderMappedOperatorAdditiveMaterials,
      maxWidth: 30,
      sortDirection: ['ascend', 'descend'],
    },
    {
      title: t('service_provider_additive_materials.provider_name_column'),
      dataIndex: 'provider_name',
      key: 'provider_name',
      sorter: (a, b) => a.provider_name.localeCompare(b.provider_name),
      sortOrder: sorter.field === 'provider_name' ? sorter.order : null,
      width: 130,
      render: (provider_name) => (
        <Typography.Text>{provider_name}</Typography.Text>
      ),
    },
    {
      title: t('specification_column'),
      dataIndex: 'spec_file',
      key: 'spec_file',
      render: (spec_file, record) => {
        if (spec_file) {
          return (
            <Button
              icon={<DownloadOutlined />}
              size="small"
              onClick={() => handleDownload(record.id)}
              loading={
                downloadMutation.isLoading &&
                downloadMutation.variables === record.id
              }
            >
              {t('download_spec_button')}
            </Button>
          )
        }

        return (
          <Upload
            customRequest={({ file, onSuccess, onError, onProgress }) => {
              uploadMutation.mutate(
                { materialId: record.id, file, onProgress },
                { onSuccess, onError },
              )
            }}
            showUploadList={false}
            accept=".png,.pdf,.jpeg,.jpg,.tiff,.tif"
          >
            <Button icon={<UploadOutlined />} size="small">
              {t('upload_spec_button', 'Upload')}
            </Button>
          </Upload>
        )
      },
    },
    {
      title: t('created_at_column'),
      dataIndex: 'created_at',
      key: 'created_at',
      sorter: (a, b) => new Date(a.created_at) - new Date(b.created_at),
      sortOrder: sorter.field === 'created_at' ? sorter.order : null,
      width: 100,
      render: (text) => dateWithTimeRenderer(t)(text),
      responsive: ['md'],
    },
    {
      key: 'action',
      title: '',
      align: 'center',
      dataIndex: 'is_archived',
      sorter: (a, b) => {
        if (a.is_archived === b.is_archived) return 0
        return a.is_archived ? 1 : -1
      },
      sortOrder: sorter.field === 'is_archived' ? sorter.order : null,
      width: 80,
      render: (is_archived, record) => (
        <Space>
          {user.isServiceProvider && (
            <Button
              type="text"
              icon={<EditOutlined />}
              onClick={(e) => {
                e.stopPropagation()
                handleEditClick(record)
              }}
            />
          )}
          <Tooltip title={t('archive_material_button')}>
            <Button
              type="text"
              icon={<UilArchiveAlt size="18" />}
              style={{
                opacity: is_archived ? 0.5 : 1,
                cursor: 'pointer',
              }}
              onClick={() => archiveMutation.mutate(record.id)}
            />
          </Tooltip>
        </Space>
      ),
    },
  ]

  return (
    <>
      <Input
        placeholder={t(
          'service_provider_additive_materials.search_placeholder',
        )}
        prefix={<SearchOutlined />}
        value={searchText}
        onChange={(e) => setSearchText(e.target.value)}
        style={{ marginBottom: 16 }}
      />
      <div ref={tableRef}>
        <StyledTable
          columns={columns}
          rowClassName={getRowClassName}
          dataSource={sortedAndFilteredData}
          loading={isLoadingServiceProviderMaterials}
          rowKey={(record) => record._id || record.id}
          pagination={{
            current: pagination.current,
            total: sortedAndFilteredData?.length || 0,
            pageSize: pagination.pageSize,
            showSizeChanger: true,
            pageSizeOptions: ['10', '20', '50', '100'],
          }}
          onChange={handleTableChange}
          size="small"
          onRow={(record) => ({
            onClick: () => onMaterialSelect?.(record.id, record),
          })}
        />
      </div>
      <Modal
        title={t(
          'my_materials.edit_material_title',
          'Edit Service Provider Additive Material',
        )}
        open={isEditModalOpen}
        onOk={handleEditOk}
        onCancel={handleEditCancel}
        confirmLoading={updateMutation.isLoading}
        okText={t('button.save', 'Save')}
        cancelText={t('button.cancel', 'Cancel')}
      >
        <Form form={form} layout="vertical">
          <Form.Item
            name="material_name"
            label={t('my_materials.material_name', 'Material Name')}
            rules={[{ required: true }]}
          >
            <Input />
          </Form.Item>
          <Form.Item
            name="available_color_ids"
            label={t('my_materials.edit_colors_label', 'Colors')}
          >
            <Select
              mode="multiple"
              placeholder={t('my_materials.select_colors', 'Select Colors')}
              options={allColors?.map((c) => ({
                label: t(`my_materials.colors.${c.name.toLowerCase()}`, c.name),
                value: c.id,
              }))}
            />
          </Form.Item>
        </Form>
      </Modal>
    </>
  )
}

export default ServiceProviderAdditiveMaterialsTable
